import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { getAuthenticatedAxios } from './axiosConfig';
import { useAuth } from '../AuthContext/AuthContext';
import { BASE_URL } from '../../Constants/constants';
import { formatDate, formatDateTimeFull, formatDateOnly } from './Dateutils';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const ICTCheckoutDashboard = () => {
    const { t } = useTranslation('ictCheckoutDashboard');
    const { currentUser } = useAuth();
    const [pendingCheckouts, setPendingCheckouts] = useState([]);
    const [selectedCheckout, setSelectedCheckout] = useState(null);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);

    const [equipmentReturned, setEquipmentReturned] = useState(false);
    const [notes, setNotes] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchPendingCheckouts = async () => {
        try {
            setLoading(true);
            const axios = getAuthenticatedAxios();
            const response = await axios.get(`${BASE_URL}/checkout/pending-ict`);

            const checkouts = Array.isArray(response.data) ? response.data : [];
            const validCheckouts = checkouts.filter(checkout =>
                checkout &&
                checkout.user_id &&
                (checkout.first_name || checkout.last_name)
            );

            setPendingCheckouts(validCheckouts);
            setError(null);
        } catch (err) {
            console.error('Error fetching pending checkouts:', err);
            setError(err.response?.data?.detail || t('errors.fetch_failed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser) {
            setError(t('errors.auth_required'));
            setLoading(false);
            return;
        }
        fetchPendingCheckouts();
        const pollingInterval = setInterval(fetchPendingCheckouts, 30000);
        return () => clearInterval(pollingInterval);
    }, [currentUser]);

    const handleProcessCheckout = (checkout) => {
        setSelectedCheckout(checkout);
        setEquipmentReturned(false);
        setNotes('');
        setShowProcessModal(true);
    };

    const handleApproveCheckout = async () => {
        try {
            setIsProcessing(true);
            const authenticatedAxios = getAuthenticatedAxios();

            const processData = {
                notes: notes || t('process.default_notes'),
                equipment_verified: equipmentReturned,
                devices: []  // no per-device verification from dashboard view
            };

            // FIXED: endpoint is /checkout/ict-checkout/{user_id} not /checkout/ict-approve
            const response = await authenticatedAxios.post(
                `${BASE_URL}/checkout/ict-checkout/${selectedCheckout.user_id}`,
                processData
            );

            if (response.data) {
                // Notify staff + ICT that ICT stage is complete
                try {
                    await axios.post(`${BASE_URL}/notify/ict-completed`, {
                        user_id: selectedCheckout.user_id,
                        notes: notes || null
                    });
                } catch (emailErr) {
                    console.warn('ICT completion email failed:', emailErr);
                }

                // ADDED: Notify Security that checkout is now in their queue
                try {
                    await axios.post(`${BASE_URL}/notify/security-pending`, {
                        user_id: selectedCheckout.user_id
                    });
                } catch (emailErr) {
                    console.warn('Security pending notification email failed:', emailErr);
                }

                setShowProcessModal(false);
                setShowConfirmModal(false);
                setSelectedCheckout(null);
                fetchPendingCheckouts();
                setError(null);
            }
        } catch (err) {
            console.error('ICT Processing Error:', err);
            setError(err.response?.data?.detail || t('errors.approve_failed'));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRejectCheckout = async () => {
        if (!rejectionReason.trim()) {
            setError(t('errors.rejection_reason_required'));
            return;
        }

        try {
            setIsProcessing(true);
            const authenticatedAxios = getAuthenticatedAxios();

            // Send rejection_reason as query param (FastAPI expects it this way)
            const response = await authenticatedAxios.post(
                `${BASE_URL}/checkout/ict-reject/${selectedCheckout.user_id}`,
                null,
                { params: { rejection_reason: rejectionReason.trim() } }
            );

            if (response.data) {
                setShowProcessModal(false);
                setShowRejectModal(false);
                setSelectedCheckout(null);
                setRejectionReason('');
                fetchPendingCheckouts();
                setError(null);
            }
        } catch (err) {
            console.error('ICT Rejection Error:', err);
            setError(err.response?.data?.detail || t('errors.reject_failed'));
        } finally {
            setIsProcessing(false);
        }
    };

    const getEmployeeName = (checkout) => {
        if (!checkout) return t('table.unknown');
        return `${checkout.first_name || ''} ${checkout.last_name || ''}`.trim() || t('table.unknown');
    };

    return (
        <Container fluid className="mt-4">
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h2>{t('heading')}</h2>
                        <Button variant="outline-primary" onClick={fetchPendingCheckouts} disabled={loading}>
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            {loading ? t('buttons.refreshing') : t('buttons.refresh')}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {loading && pendingCheckouts.length === 0 ? (
                        <Alert variant="info">
                            <div className="d-flex align-items-center">
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">{t('loading.text')}</span>
                                </div>
                                {t('loading.message')}
                            </div>
                        </Alert>
                    ) : pendingCheckouts.length === 0 ? (
                        <Alert variant="success">
                            <i className="bi bi-check-circle me-2"></i>
                            {t('no_pending')}
                        </Alert>
                    ) : (
                        <>
                            <div className="mb-3">
                                <Row>
                                    <Col md={4}>
                                        <Card className="border-warning">
                                            <Card.Body>
                                                <h6 className="text-muted">{t('stats.pending')}</h6>
                                                <h2 className="mb-0">{pendingCheckouts.length}</h2>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </div>

                            <Table striped bordered hover responsive>
                                <thead>
                                <tr>
                                    <th>{t('table.headers.employee')}</th>
                                    <th>{t('table.headers.email')}</th>
                                    <th>{t('table.headers.equipment')}</th>
                                    <th>{t('table.headers.initiated')}</th>
                                    <th>{t('table.headers.actions')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {pendingCheckouts.map((checkout) => (
                                    <tr key={checkout.user_id}>
                                        <td>{getEmployeeName(checkout)}</td>
                                        <td>{checkout.email || t('table.no_email')}</td>
                                        <td>{checkout.equipment_count || 0}</td>
                                        <td>{formatDate(checkout.created_at)}</td>
                                        <td>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleProcessCheckout(checkout)}
                                            >
                                                {t('buttons.process')}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Process Modal */}
            <Modal show={showProcessModal} onHide={() => setShowProcessModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('modal.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCheckout && (
                        <>
                            <Card className="mb-3">
                                <Card.Body>
                                    <h5>{t('modal.employee_info')}</h5>
                                    <p className="mb-1"><strong>{t('modal.name')}</strong> {getEmployeeName(selectedCheckout)}</p>
                                    <p className="mb-1"><strong>{t('modal.email')}</strong> {selectedCheckout.email}</p>
                                    <p className="mb-1"><strong>{t('modal.equipment_count')}</strong> {selectedCheckout.equipment_count || 0}</p>
                                    <p className="mb-0"><strong>{t('modal.return_date')}</strong> {formatDate(selectedCheckout.user_return_date)}</p>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3">
                                <Card.Header><h6 className="mb-0">{t('verification.title')}</h6></Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="equipment-verified"
                                            label={t('verification.equipment_verified')}
                                            checked={equipmentReturned}
                                            onChange={(e) => setEquipmentReturned(e.target.checked)}
                                        />
                                        <Form.Text className="text-muted">{t('verification.verification_note')}</Form.Text>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>{t('form.notes_label')}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            placeholder={t('form.notes_placeholder')}
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                        />
                                    </Form.Group>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={() => { setShowProcessModal(false); setShowRejectModal(true); }}>
                        {t('buttons.reject')}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowProcessModal(false)}>
                        {t('buttons.cancel')}
                    </Button>
                    <Button variant="success" onClick={() => setShowConfirmModal(true)} disabled={!equipmentReturned}>
                        {t('buttons.approve')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirm Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('confirmation.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCheckout && (
                        <>
                            <Alert variant="warning"><strong>{t('confirmation.warning')}</strong></Alert>
                            <div className="mb-3">
                                <h6>{t('confirmation.summary')}</h6>
                                <ul>
                                    <li><strong>{t('confirmation.employee')}</strong> {getEmployeeName(selectedCheckout)}</li>
                                    <li><strong>{t('confirmation.equipment_verified')}</strong> {equipmentReturned ? t('status.yes') : t('status.no')}</li>
                                    {notes && <li><strong>{t('confirmation.notes')}</strong> {t('confirmation.notes_provided')}</li>}
                                </ul>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>{t('buttons.back')}</Button>
                    <Button variant="primary" onClick={handleApproveCheckout} disabled={isProcessing}>
                        {isProcessing ? t('buttons.processing') : t('buttons.confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reject Modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">{t('rejection.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCheckout && (
                        <>
                            <Alert variant="danger"><strong>{t('rejection.warning')}</strong></Alert>
                            <p className="mb-3"><strong>{t('rejection.employee')}</strong> {getEmployeeName(selectedCheckout)}</p>
                            <Form.Group>
                                <Form.Label>{t('rejection.reason_label')} *</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder={t('rejection.reason_placeholder')}
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    required
                                />
                                <Form.Text className="text-muted">{t('rejection.reason_note')}</Form.Text>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>{t('buttons.cancel')}</Button>
                    <Button variant="danger" onClick={handleRejectCheckout} disabled={isProcessing || !rejectionReason.trim()}>
                        {isProcessing ? t('buttons.processing') : t('buttons.confirm_reject')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ICTCheckoutDashboard;